import type { VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';

import type { ButtonProps } from '@/app/components/button';
import { Container } from '@/app/components/layouts/container';
import { Section, type SectionAs } from '@/app/components/layouts/section';
import type { Theme } from '@/app/types/styles/theme';
import { cn } from '@/app/utils/cn';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import type { DataGAProps } from '@/app/utils/createDataGA';

export type HeaderProps = {
  title?: string;
  theme?: Theme;
  className?: string;
  containerClassName?: string;
};

export type HeaderWithButtonsProps = HeaderProps & {
  description?: string;
  buttons?: ButtonProps[];
  buttonChildren?: ReactNode;
  as?: SectionAs;
};

export interface ContentProps {
  content: string | ReactNode;
  buttons?: ButtonProps[];
}

export type HeaderWithContentProps = HeaderProps & {
  lead?: string;
  content1?: ContentProps | null;
  content2?: ContentProps | null;
  buttons?: ButtonProps[];
  layout?: 'two-column' | 'center';
  leadClassName?: string;
  contentClassName?: string;
  content2ClassName?: string;
  testId?: string;
  tracking?: Omit<DataGAProps, 'click'>;
};

export const HeaderStyled = cnva('text-body-1 where-span:text-inline-wrap', {
  variants: {
    theme: {
      light: 'text-gray-900',
      dark: 'text-white',
    },
    layout: {
      'two-column': 'flex flex-col gap-y-6 lg:flex-row lg:gap-x-12',
      center: 'text-center xl:mx-auto xl:max-w-1/2',
      none: 'space-y-4',
    },
  },
  defaultVariants: {
    theme: 'light',
    layout: 'none',
  },
});

interface HeaderWrapProps extends VariantProps<typeof HeaderStyled> {
  children: ReactNode;
  className?: string;
  containerClassName?: string;
  testId?: string;
  as?: SectionAs;
}

export const HeaderWrap = ({
  children,
  theme = 'light',
  layout = 'none',
  className,
  containerClassName,
  testId = 'heading',
  ...props
}: HeaderWrapProps) => {
  return (
    <Section
      className={cn('py-6 xl:py-10', className)}
      size="sm"
      {...createDataAttributes({
        test: testId,
      })}
      {...props}
    >
      <Container
        className={cn(HeaderStyled({ theme, layout }), containerClassName)}
      >
        {children}
      </Container>
    </Section>
  );
};
